import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useParams } from "react-router";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ProductCategoryApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import ProductCategoryForm from "../../components/ProductCategoryForm";

interface MatchParams {
	categoryId: string;
}

const OrganizationProductCategoriesEditPage: FC<RouteComponentProps> = () => {
	const [productCategory, setProductCategory] = useState<ProductCategoryApi | undefined>(undefined);
	const [customFieldConfig, setCustomFieldConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const { categoryId } = useParams<MatchParams>();
	const { t } = useTranslation();
	const history = useHistory();
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		const getCategory = async () => {
			const [catParams, resourceParams] = getParams();
			const [category, cf] = await Promise.all([
				api.organization().getProductCategory(Number(categoryId), catParams),
				api.organization().getResourceTypes(resourceParams),
			]);
			setProductCategory(category);
			setCustomFieldConfig(cf.custom_fields);
			handleChangeTabTitle(`${category.name} | ${t("modules.category.header.title")}`);
		};
		getCategory();
	}, []);

	const getParams = () => {
		const catParams: Record<string, any> = { include: "all" };
		const resourceParams: Record<string, any> = {
			resource_name: "PRODUCT_CATEGORY",
			custom_field_resource_type: "PRODUCT_CATEGORY",
		};
		return [catParams, resourceParams];
	};

	if (!productCategory || !customFieldConfig) return <LoadingContainer />;

	const deleteProductCategoryHandler = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await api.organization().removeProductCategory(Number(productCategory.id));
			addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
			history.push(`/${organization.id}/products/categories`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const activateProductCategoryHandler = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.activate", { ns: "lib" }),
			});
			await api.organization().restoreProductCategory(Number(productCategory.id));
			addSuccessFlash(t("common.flash.restored", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [];

	if (productCategory.status === "ENABLED")
		buttons.push({
			title: t("common.action.remove", { ns: "lib" }),
			action: deleteProductCategoryHandler,
			dropdown: true,
		});

	if (productCategory.status === "DELETED")
		buttons.push({
			title: t("common.action.activate", { ns: "lib" }),
			action: activateProductCategoryHandler,
			dropdown: true,
		});

	return (
		<>
			<Header title={t("modules.product_category.header.title")} buttons={buttons} />
			<ProductCategoryForm productCategory={productCategory} customFieldsConfig={customFieldConfig} />
		</>
	);
};
export default OrganizationProductCategoriesEditPage;
